import { useEffect, useMemo, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import Api from '../api/api';
import image from '../assets/background.webp';
import image5 from '../assets/pexels-donaldtong94-109669.jpg';
import image4 from '../assets/pexels-edgarcolomba-2671089.jpg';
import image3 from '../assets/pexels-nathan-engel-50858-436413.jpg';
import image2 from '../assets/pexels-pixabay-268883.jpg';
import { useCPRWSData } from '../hooks/useCPRWSData';
import { useScript } from '../hooks/useScript';
import {
  Config,
  ICartState,
  ICenter,
  ILoginState,
  IOnLoginPayload,
  IOnLoginViaWebViewAuthPayload,
  IPositionInfo,
} from '../interfaces';
import Banner from './banner';
import CenterSelection from './centerSelection';
import FilmList from './film-list';
import Footer from './footer';
import Navigation from './navigation';
import RetailDetails from './retail-details';
import VoucherList from './voucher-list';
import Webshop from './webshop';

declare const config: Config;
export const IFRAME_LANGUAGES = ['DE', 'EN'];

function App() {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('DE');
  const [centers, setCenters] = useState<ICenter[]>();
  const status = useScript(`${config.apiService.webshop.endpoint}/assets/js/webshop-scripts.js`);
  const isScriptReady = useMemo(() => status === 'ready', [status]);
  const [loginState, setLoginState] = useState<ILoginState>();
  const [cartState, setCartState] = useState<ICartState>();
  const withDialog = window.location.pathname.includes('withdialog');
  const centerId = window.location.pathname.split('/')[2];
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const images = [image, image2, image3, image4, image5];

  const selectedCenter = useMemo(() => centers?.find((center) => center.winticketId === centerId), [centers, centerId]);

  const cprwsData = useCPRWSData();

  const getCenters = async (centerid?: string) => {
    try {
      const centers = await Api.retrieveCenters();
      if (centers) {
        if (centerid) {
          const matchingCenter = centers.find((center) => center.winticketId === centerid);
          if (!matchingCenter) {
            console.warn(`No center found with id: ${centerid}`);
          }
        }
        setCenters(centers);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to get centers:', error);
      setIsLoading(false);
    }
  };

  const handleCenterSelection = (selectedCenterWintickedId: string) => {
    const selectedCenter = centers?.find((center) => center.winticketId === selectedCenterWintickedId);

    if (selectedCenter) {
      if (window.location.pathname.includes('seatingplan') && !withDialog) {
        window.location.href = '/';
      }
    }
  };

  const processIframeScrollPositionAdvice = (positionInfo: IPositionInfo): void => {
    const elements = document.getElementsByClassName('iframe-webshop') as HTMLCollectionOf<HTMLElement> | null;

    if (elements && elements.length > 0) {
      const iframe = elements[0];
      const positionAdvisedToBeVisible = positionInfo.advisedVisiblePosition;
      const desiredVisiblePosition = iframe.offsetTop + Number(positionAdvisedToBeVisible);
      window.focus();
      window.scrollTo(0, desiredVisiblePosition);
    }
  };

  // Initialize centers and language based on CPRWS data
  useEffect(() => {
    if (cprwsData) {
      void getCenters(cprwsData.centerid);
      if (cprwsData.parameters?.language && IFRAME_LANGUAGES.includes(cprwsData.parameters.language.toUpperCase())) {
        setSelectedLanguage(cprwsData.parameters.language.toUpperCase());
      }
    } else {
      void getCenters(undefined);
    }
  }, [cprwsData]);

  useEffect(() => {
    if (typeof window.Compeso?.loadIFrameResizer === 'function') {
      window.Compeso.loadIFrameResizer();
    }
  }, []);

  useEffect(() => {
    if (
      isScriptReady &&
      typeof window.Compeso?.setLoginChangedCallback === 'function' &&
      typeof window.Compeso.getLogin === 'function'
    ) {
      window.Compeso.setLoginChangedCallback((info) => {
        console.log(' Login changed in iframe: ', info);
        setLoginState(info);
      });
      window.Compeso.getLogin();
    }

    if (
      isScriptReady &&
      typeof window.Compeso?.setCartChangedCallback === 'function' &&
      typeof window.Compeso.getCart === 'function'
    ) {
      window.Compeso.setCartChangedCallback((info) => setCartState(info));
      window.Compeso.getCart();
    }

    if (isScriptReady && typeof window.Compeso?.setScrollPositionAdviceCallback === 'function') {
      window.Compeso.setScrollPositionAdviceCallback((info) =>
        processIframeScrollPositionAdvice(info as IPositionInfo),
      );
    }

    if (isScriptReady && typeof window.Compeso?.setLoginFieldsDisplayedCallback === 'function') {
      window.Compeso.setLoginFieldsDisplayedCallback(() => {
        console.log('Webshop in iframe is displaying login fields.');
      });
    }

    if (isScriptReady && typeof window.Compeso?.setOnLoginCallback === 'function') {
      window.Compeso.setOnLoginCallback((data) => {
        console.log('User logged in inside iframe.', data);
      });
    }

    if (isScriptReady && typeof window.Compeso?.setOnLogoutCallback === 'function') {
      window.Compeso.setOnLogoutCallback(() => console.log('User logged out inside iframe.'));
    }

    if (isScriptReady && typeof window.Compeso?.setOnLoginViaWebViewAuthCallback === 'function') {
      window.Compeso.setOnLoginViaWebViewAuthCallback((data) => {
        console.log(`User was ${!data?.isLoggedIn ? 'not ' : ''}successfully logged in.`);
      });
    }
  }, [isScriptReady]);

  useEffect(() => {
    if (selectedCenter || isLoading) return;

    if (window.location.pathname !== '/center-selection') {
      window.location.href = '/center-selection';
    }
  }, [selectedCenter, isLoading]);

  if (status === 'error') {
    return <div>Error loading script.</div>;
  }

  if (!isScriptReady) {
    return <div>Loading script...</div>;
  }

  if (isLoading) {
    return <div>Loading centers...</div>;
  }

  return (
    <div className='appContainer'>
      <div className='blurred-background-container'>
        <Navigation
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          centerId={selectedCenter?.winticketId}
          centerName={selectedCenter?.name}
          isLoggedIn={loginState !== undefined}
          cartState={cartState}
        />
        {!selectedCenter ? (
          <CenterSelection
            centers={centers ?? []}
            selectedLanguage={selectedLanguage}
            onCenterSelect={handleCenterSelection}
          />
        ) : (
          <>
            {location.pathname !== '/center-selection' && <Banner images={images} interval={5000} />}
            <Switch>
              <Route
                path='/center-selection'
                render={() => (
                  <CenterSelection
                    centers={centers ?? []}
                    selectedLanguage={selectedLanguage}
                    onCenterSelect={handleCenterSelection}
                  />
                )}
              />

              <Route
                path='/center/:centerId/film-list'
                render={() => <FilmList selectedCenter={selectedCenter} selectedLanguage={selectedLanguage} />}
              />
              <Route
                path='/center/:centerId/withdialog/film-list'
                render={() => (
                  <FilmList selectedCenter={selectedCenter} selectedLanguage={selectedLanguage} withDialog={true} />
                )}
              />
              <Route
                path='/center/:centerId/withdialog'
                render={() => (
                  <FilmList selectedCenter={selectedCenter} selectedLanguage={selectedLanguage} withDialog={true} />
                )}
              />
              <Route
                path='/center/:centerId/voucher-list'
                render={() => <VoucherList selectedCenter={selectedCenter} />}
              />
              <Route path='/center/:centerId/retail-details' render={() => <RetailDetails />} />
              <Route
                path='/center/:centerId/:page'
                render={(props) => (
                  <Webshop {...props} selectedCenter={selectedCenter} selectedLanguage={selectedLanguage} />
                )}
              />
            </Switch>
            <Footer centerId={selectedCenter.winticketId} />
          </>
        )}
      </div>
    </div>
  );
}

export default App;

declare global {
  interface Window {
    Compeso:
      | undefined
      | {
          loadIFrameResizer(): void;
          updateOrInitializeIFrameResizer(): void;
          loadContent(shopbaseurl: string): void;
          getParentIFrame(): unknown;

          getLogin(): unknown;
          setLoginChangedCallback(callback: (info: ILoginState | undefined) => void): boolean;

          getCart(): unknown;
          setCartChangedCallback(callback: (info: ICartState | undefined) => void): boolean;
          setLoginFieldsDisplayedCallback(callback: () => void): boolean;
          setOnLoginCallback(callback: (data: IOnLoginPayload | undefined) => void): boolean;
          setOnLogoutCallback(callback: () => void): boolean;
          setOnLoginViaWebViewAuthCallback(
            callback: (data: IOnLoginViaWebViewAuthPayload | undefined) => void,
          ): boolean;

          setScrollPositionAdviceCallback(callback: unknown): boolean;
        };

    iFrameResize: unknown;
  }
}
